import { useState } from 'react';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { Alert, Box, Button, Card, Chip, Stack, Typography } from '@mui/material';
import type { ArchiveStats, MatchKind, SearchGroup, SearchResponse } from '../api/types';
import { HighlightedSnippet } from './HighlightedText';
import { HitPreview } from './HitPreview';

const visibleHits = 4;

/**
 * How each retrieval tier is labelled. `exact` has no badge — it is what a
 * search is expected to return — and every looser tier does, because a hit found
 * inside a compound or with a word missing must not read as an ordinary match.
 * The server decides the tier; nothing here re-derives it from the query.
 */
const matchBadge: Record<Exclude<MatchKind, 'exact'>, { label: string; title: string }> = {
  prefix: { label: 'Wortform', title: 'Gefunden über eine andere Endung des Suchbegriffs' },
  substring: { label: 'Teilwort', title: 'Gefunden innerhalb eines zusammengesetzten Wortes' },
  partial: { label: 'Teiltreffer', title: 'Diese Seite enthält nur einen Teil der Suchbegriffe' }
};

function MatchChip({ kind, sx }: { kind?: MatchKind; sx?: object }) {
  if (!kind || kind === 'exact') return null;
  const badge = matchBadge[kind];

  return <Chip label={badge.label} title={badge.title} size="small" color="warning" variant="outlined" sx={{ flexShrink: 0, ...sx }} />;
}

/**
 * The archive folder a document was found in, shown only when it says something.
 *
 * `.` is the archive root, and rendering it as "/" next to a folder icon tells
 * the reader nothing they did not already know — it is chrome that looks like
 * information. Real folders (KW29, KW28-2) are worth the line, because in a
 * corpus organised by delivery batch that is how people locate a binder.
 */
function FolderLine({ path }: { path: string }) {
  if (path === '.') return null;

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" color="text.secondary">
      <FolderOutlinedIcon sx={{ fontSize: 15 }} />
      <Typography variant="caption" noWrap>{path}</Typography>
    </Stack>
  );
}

function ResultGroup({ group, query, onOpen }: {
  group: SearchGroup;
  query: string;
  onOpen: (pdfId: number, page: number) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const hits = expanded ? group.hits : group.hits.slice(0, visibleHits);

  return (
    <Card component="article" sx={{ overflow: 'hidden', backgroundColor: 'background.paper' }}>
      <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
          <Stack direction="row" spacing={1.25} sx={{ minWidth: 0 }}>
            <DescriptionOutlinedIcon color="primary" />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h3" noWrap title={group.file_name}>{group.file_name}</Typography>
              <FolderLine path={group.folder_path} />
            </Box>
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
            <MatchChip kind={group.match_kind} />
            <Chip size="small" label={`${group.total_pages} Treffer`} color="primary" variant="outlined" />
          </Stack>
        </Stack>
      </Box>
      <Stack>
        {hits.map((hit) => (
          <Button
            key={hit.page_id}
            color="inherit"
            onClick={() => onOpen(group.pdf_id, hit.page_number)}
            sx={{
              justifyContent: 'flex-start',
              alignItems: 'center',
              textAlign: 'left',
              borderRadius: 0,
              px: { xs: 2, sm: 2.5 },
              py: 1.5,
              gap: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              '&:hover': { backgroundColor: (theme) => theme.palette.action.hover }
            }}
          >
            {/* The crop first, because it is what the eye should land on: the
                point of the geometry work is judging a result without opening
                it. The snippet stays alongside — it is what can be read and
                copied, and it is all there is for a page with no coordinates. */}
            <HitPreview pdfId={group.pdf_id} page={hit.page_number} query={query} />
            <Stack sx={{ minWidth: 0, flex: 1, gap: 0.5 }}>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Chip label={`S. ${hit.page_number}`} size="small" sx={{ flexShrink: 0 }} />
                <MatchChip kind={hit.match_kind} />
              </Stack>
              <Typography component="span" variant="body2" sx={{ color: 'text.primary', lineHeight: 1.55 }}>
                <HighlightedSnippet text={hit.snippet ?? ''} />
              </Typography>
            </Stack>
          </Button>
        ))}
      </Stack>
      {!expanded && group.hits.length > visibleHits && (
        <Button fullWidth endIcon={<KeyboardArrowDownRoundedIcon />} onClick={() => setExpanded(true)} sx={{ borderRadius: 0, py: 1.25 }}>
          {group.hits.length - visibleHits} weitere Treffer
        </Button>
      )}
    </Card>
  );
}

/**
 * Pages that exist in the archive but not in the index. Without this line the
 * page count and the searchable count disagree with no explanation, and a term
 * sitting on an unread page looks like a broken search rather than a gap in the
 * corpus.
 */
function CoverageNote({ stats }: { stats: ArchiveStats | null }) {
  if (!stats) return null;
  const missing = stats.needs_review + stats.pending;
  if (!missing) return null;

  const parts = [
    stats.needs_review ? `${stats.needs_review} zur Prüfung` : '',
    stats.pending ? `${stats.pending} noch nicht verarbeitet` : ''
  ].filter(Boolean);

  return (
    <Typography variant="caption" color="text.secondary">
      {missing} Seiten sind nicht durchsuchbar ({parts.join(', ')}) und können hier nicht gefunden werden.
    </Typography>
  );
}

export function SearchResults({ data, stats, onOpen }: {
  data: SearchResponse;
  stats: ArchiveStats | null;
  onOpen: (pdfId: number, page: number) => void;
}) {
  if (!data.groups.length) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h2">Keine Treffer</Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>Für „{data.query}“ wurden keine passenden Seiten gefunden.</Typography>
        <Box sx={{ mt: 1 }}><CoverageNote stats={stats} /></Box>
      </Box>
    );
  }

  // Every card looser than exact means no page carried the whole query. Saying so
  // once is clearer than leaving the reader to infer it from the badges.
  const onlyPartial = data.groups.every((group) => group.match_kind === 'partial');

  return (
    <Stack spacing={1.5}>
      {onlyPartial && (
        <Alert severity="info">
          Keine Seite enthält alle Suchbegriffe. Angezeigt werden Seiten mit einem Teil davon.
        </Alert>
      )}
      <Box>
        <Typography variant="body2" color="text.secondary">
          {data.total_pages} Seiten in {data.groups.length} Dokumenten · {data.took_ms} ms
        </Typography>
        <CoverageNote stats={stats} />
      </Box>
      {data.groups.map((group) => (
        <ResultGroup key={group.pdf_id} group={group} query={data.query} onOpen={onOpen} />
      ))}
    </Stack>
  );
}
